import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { usePageTitle } from '@/hooks/use-page-title'
import { useIsMobile } from '@/hooks/use-mobile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Progress } from '@/components/shadcn/progress'

import { Button } from '@/components/shadcn/button'
import { Database, Clock, Key, BarChart3 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/shadcn/chart'
import { listMemoryLibraryIds } from '@/lib/api/memory'
import { fetchTelemetryOverview, fetchTelemetryTimeseries, fetchTelemetryNewMessagesTimeseries } from '@/lib/api/telemetry'

function isoToLocalDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
}

function isoToLocalDateTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

function toIsoOrUndefined(v: string): string | undefined {
  if (!v) return undefined
  try {
    const d = new Date(v)
    if (isNaN(d.getTime())) return undefined
    return d.toISOString()
  } catch {
    return undefined
  }
}

export default function OverviewPage() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const isMobile = useIsMobile()
  
  // 设置页面标题
  useDocumentTitle(t('overview.title'))
  
  // 在移动端设置页面标题到顶栏
  usePageTitle('overview.title')
  
  // 获取当前语言前缀
  const getCurrentLanguagePrefix = () => {
    const path = location.pathname
    if (path.startsWith('/en')) return '/en'
    if (path.startsWith('/zh')) return '/zh'
    return '/zh' // 默认中文
  }

  const languagePrefix = getCurrentLanguagePrefix()
  
  const [startLocal, setStartLocal] = useState<string>(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return isoToLocalDateTime(d.toISOString())
  })
  const [endLocal, setEndLocal] = useState<string>(() => isoToLocalDateTime(new Date().toISOString()))

  const { data: allMemoryIds } = useQuery({
    queryKey: ['memory-library-ids'],
    queryFn: () => listMemoryLibraryIds(),
  })

  const commonParams = useMemo(() => {
    return {
      start: toIsoOrUndefined(startLocal),
      end: toIsoOrUndefined(endLocal),
      memory_ids: allMemoryIds || [],
    }
  }, [startLocal, endLocal, allMemoryIds])

  const { data: overview } = useQuery({
    queryKey: ['telemetry-overview-dashboard', commonParams],
    queryFn: () => fetchTelemetryOverview(commonParams),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: ts } = useQuery({
    queryKey: ['telemetry-timeseries-dashboard', commonParams],
    queryFn: () => fetchTelemetryTimeseries({ ...commonParams, interval: 'day' }),
    enabled: (allMemoryIds || []).length > 0,
  })

  // 生成固定45个数据点的API调用趋势数据，基于真实数据填充
  const generateApiTrendData = useMemo(() => {
    const dataPoints = 45
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - dataPoints + 1)
    
    // 创建真实数据的映射，按日期分组
    const realDataMap = new Map()
    let realDataStats = { min: 0, max: 0, avg: 0 }
    
    if (ts && ts.length > 0) {
      ts.forEach(item => {
        const dateKey = new Date(item.time).toDateString()
        realDataMap.set(dateKey, item)
      })
      
      // 计算真实数据的统计信息
      const counts = ts.map(item => item.count).filter(count => count > 0)
      if (counts.length > 0) {
        realDataStats = {
          min: Math.min(...counts),
          max: Math.max(...counts),
          avg: counts.reduce((sum, count) => sum + count, 0) / counts.length
        }
      }
    }
    
    // 计算Y轴最大值
    const hasRealData = realDataStats.max > 0
    let yAxisMax = 100 // 默认值
    
    if (hasRealData) {
      // 如果有真实数据，设置为最大值的1.2倍，但不超过10000
      yAxisMax = Math.min(10000, Math.max(100, realDataStats.max * 1.2))
    }
    
    const trendData = []
    for (let i = 0; i < dataPoints; i++) {
      const currentDate = new Date(startDate)
      currentDate.setDate(startDate.getDate() + i)
      const dateKey = currentDate.toDateString()
      
      // 检查是否有真实数据
      const realData = realDataMap.get(dateKey)
      
      if (realData) {
        // 使用真实数据
        trendData.push({
          time: currentDate.toISOString(),
          count: realData.count,
          avg_ms: realData.avg_ms,
          p95_ms: realData.p95_ms,
          p99_ms: realData.p99_ms,
        })
      } else {
        // 占位符高度 - 设置得非常小，几乎看不见
        let placeholderHeight = 0.5 // 默认最小值，几乎看不见
        
        if (realDataStats.avg > 0) {
          // 如果有真实数据，占位符高度为真实数据平均值的0.1%，但不超过0.5
          placeholderHeight = Math.min(1, Math.max(0.1, realDataStats.avg * 0.001))
        } else if (realDataStats.max > 0) {
          // 如果只有最大值，占位符高度为最大值的0.05%
          placeholderHeight = Math.min(1, Math.max(0.1, realDataStats.max * 0.0005))
        }
        
        // 为占位符也生成一些合理的性能数据，让tooltip更真实
        const mockAvgMs = Math.floor(Math.random() * 200) + 50
        const mockP95Ms = mockAvgMs + Math.floor(Math.random() * 300) + 100
        const mockP99Ms = mockP95Ms + Math.floor(Math.random() * 200) + 50
        
        trendData.push({
          time: currentDate.toISOString(),
          count: placeholderHeight,
          avg_ms: mockAvgMs,
          p95_ms: mockP95Ms,
          p99_ms: mockP99Ms,
        })
      }
    }
    
    return { data: trendData, yAxisMax }
  }, [ts])

  const { data: newMsgTs } = useQuery({
    queryKey: ['telemetry-new-messages-dashboard', commonParams],
    queryFn: () => fetchTelemetryNewMessagesTimeseries({ ...commonParams, interval: 'day' }),
    enabled: (allMemoryIds || []).length > 0,
  })

  // 生成固定45个数据点的记忆创建趋势数据，基于真实数据填充
  const generateMemoryTrendData = useMemo(() => {
    const dataPoints = 45
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - dataPoints + 1)
    
    // 创建真实数据的映射，按日期分组
    const realDataMap = new Map()
    let realDataStats = { min: 0, max: 0, avg: 0 }
    
    if (newMsgTs && newMsgTs.length > 0) {
      newMsgTs.forEach(item => {
        const dateKey = new Date(item.time).toDateString()
        realDataMap.set(dateKey, item)
      })
      
      // 计算真实数据的统计信息
      const counts = newMsgTs.map(item => item.count).filter(count => count > 0)
      if (counts.length > 0) {
        realDataStats = {
          min: Math.min(...counts),
          max: Math.max(...counts),
          avg: counts.reduce((sum, count) => sum + count, 0) / counts.length
        }
      }
    }
    
    // 计算Y轴最大值
    const hasRealData = realDataStats.max > 0
    let yAxisMax = 50 // 默认值
    
    if (hasRealData) {
      // 如果有真实数据，设置为最大值的1.2倍，但不超过5000
      yAxisMax = Math.min(5000, Math.max(50, realDataStats.max * 1.2))
    }
    
    const trendData = []
    for (let i = 0; i < dataPoints; i++) {
      const currentDate = new Date(startDate)
      currentDate.setDate(startDate.getDate() + i)
      const dateKey = currentDate.toDateString()
      
      // 检查是否有真实数据
      const realData = realDataMap.get(dateKey)
      
      if (realData) {
        // 使用真实数据
        trendData.push({
          time: currentDate.toISOString(),
          count: realData.count,
        })
      } else {
        // 占位符高度 - 设置得非常小，几乎看不见
        let placeholderHeight = 0.1 // 默认最小值，几乎看不见
        
        if (realDataStats.avg > 0) {
          // 如果有真实数据，占位符高度为真实数据平均值的0.1%，但不超过0.5
          placeholderHeight = Math.min(0.5, Math.max(0.1, realDataStats.avg * 0.001))
        } else if (realDataStats.max > 0) {
          // 如果只有最大值，占位符高度为最大值的0.05%
          placeholderHeight = Math.min(0.3, Math.max(0.1, realDataStats.max * 0.0005))
        }
        
        trendData.push({
          time: currentDate.toISOString(),
          count: placeholderHeight,
        })
      }
    }
    
    return { data: trendData, yAxisMax }
  }, [newMsgTs])

  return (
    <div className={`space-y-6 ${isMobile ? 'pt-2' : 'pt-8'}`}>
      {/* Header */}
      <div className="flex flex-col items-stretch space-y-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <h1 className={`text-2xl font-semibold tracking-tight ${isMobile ? 'hidden' : ''}`}>{t('overview.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('overview.description')}
          </p>
        </div>
        {/* 生成报告功能暂时不可用，已隐藏 */}
        {/* <div className="flex mt-4 sm:mt-0 gap-2 items-center">
          <input
            className="border rounded px-2 py-1 text-sm"
            type="datetime-local"
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
          />
          <span className="text-muted-foreground text-xs">{t('overview.to')}</span>
          <input
            className="border rounded px-2 py-1 text-sm"
            type="datetime-local"
            value={endLocal}
            onChange={(e) => setEndLocal(e.target.value)}
          />
          
          <Button className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {t('overview.generateReport')}
          </Button> 
        </div>*/}
      </div>

      {/* Telemetry Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('overview.cards.totalRequests')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('overview.cards.totalRequestsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.total_requests?.toLocaleString?.() ?? '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('overview.cards.avgRps')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('overview.cards.avgRpsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview ? overview.rps_avg.toFixed(3) : '—'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('overview.cards.p95P99')}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">{t('overview.cards.p95P99Desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview ? `${Math.round(overview.duration_ms.p95)} / ${Math.round(overview.duration_ms.p99)}` : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
            <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-4 sm:py-5">
              <CardTitle>{t('overview.charts.apiCallTrend')}</CardTitle>
              <CardDescription>{t('overview.charts.apiCallTrendDesc')}</CardDescription>
            </div>
            <div className="flex">
              <button
                data-active
                className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l data-[active=true]:bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-5"
              >
                <span className="text-xs text-muted-foreground">{t('overview.charts.apiCalls')}</span>
                <span className="text-lg font-bold leading-none sm:text-2xl">
                  {(ts || []).reduce((a, b) => a + (b.count || 0), 0).toLocaleString()}
                </span>
              </button>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:p-6">
            <ChartContainer
              config={{
                views: { label: t('overview.charts.apiCalls') },
                count: { label: 'count', color: 'hsl(var(--chart-1))' },
              }}
              className="aspect-auto h-[300px] w-full"
            >
              <BarChart accessibilityLayer data={generateApiTrendData.data} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="time"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={16}
                  tickFormatter={(value) => isoToLocalDate(value)}
                />
                <YAxis
                  domain={[0, generateApiTrendData.yAxisMax]}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent 
                      className="w-[150px]" 
                      nameKey="count" 
                      labelFormatter={(value) => isoToLocalDate(value)}
                      formatter={(value, _name, props) => {
                        // 检查是否为真实数据
                        const hasRealData = ts && ts.some(realItem => {
                          const realDate = new Date(realItem.time).toDateString()
                          const currentDate = new Date(props.payload.time).toDateString()
                          return realDate === currentDate
                        })
                        
                        if (hasRealData) {
                          return [Math.floor(Number(value)), t('overview.charts.apiCalls')]
                        } else {
                          return [0, `${t('overview.charts.apiCalls')} (${t('overview.charts.placeholder')})`]
                        }
                      }}
                    />
                  }
                />
                <Bar 
                  dataKey="count" 
                  fill="var(--color-count)"
                  minPointSize={2}
                >
                  {generateApiTrendData.data.map((entry, index) => {
                    // 判断是否为真实数据（通过检查是否有对应的真实数据）
                    const hasRealData = ts && ts.some(realItem => {
                      const realDate = new Date(realItem.time).toDateString()
                      const currentDate = new Date(entry.time).toDateString()
                      return realDate === currentDate
                    })
                    
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={hasRealData ? "var(--color-count)" : "hsl(var(--chart-1) / 0.4)"} 
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
            <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-4 sm:py-5">
              <CardTitle>{t('overview.charts.memoryCreationTrend')}</CardTitle>
              <CardDescription>{t('overview.charts.memoryCreationTrendDesc')}</CardDescription>
            </div>
            <div className="flex">
              <button
                data-active
                className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l data-[active=true]:bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-5"
              >
                <span className="text-xs text-muted-foreground">{t('overview.charts.memoryCreation')}</span>
                <span className="text-lg font-bold leading-none sm:text-2xl">
                  {(newMsgTs || []).reduce((a, b) => a + (b.count || 0), 0).toLocaleString()}
                </span>
              </button>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:p-6">
            <ChartContainer
              config={{ views: { label: t('overview.charts.memoryCreation') }, count: { label: 'count', color: 'hsl(var(--chart-2))' } }}
              className="aspect-auto h-[300px] w-full"
            >
              <BarChart accessibilityLayer data={generateMemoryTrendData.data} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="time"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={16}
                  tickFormatter={(value) => isoToLocalDate(value)}
                />
                <YAxis
                  domain={[0, generateMemoryTrendData.yAxisMax]}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                />
                <ChartTooltip 
                  content={
                    <ChartTooltipContent 
                      className="w-[150px]" 
                      nameKey="count" 
                      labelFormatter={(value) => isoToLocalDate(value)}
                      formatter={(value, _name, props) => {
                        // 检查是否为真实数据
                        const hasRealData = newMsgTs && newMsgTs.some(realItem => {
                          const realDate = new Date(realItem.time).toDateString()
                          const currentDate = new Date(props.payload.time).toDateString()
                          return realDate === currentDate
                        })
                        
                        if (hasRealData) {
                          return [Math.floor(Number(value)), t('overview.charts.memoryCreation')]
                        } else {
                          return [0, `${t('overview.charts.memoryCreation')} (${t('overview.charts.placeholder')})`]
                        }
                      }}
                    />
                  } 
                />
                <Bar 
                  dataKey="count" 
                  fill="var(--color-count)"
                  minPointSize={2}
                >
                  {generateMemoryTrendData.data.map((entry, index) => {
                    // 判断是否为真实数据（通过检查是否有对应的真实数据）
                    const hasRealData = newMsgTs && newMsgTs.some(realItem => {
                      const realDate = new Date(realItem.time).toDateString()
                      const currentDate = new Date(entry.time).toDateString()
                      return realDate === currentDate
                    })
                    
                    return (
                      <Cell 
                        key={`memory-cell-${index}`} 
                        fill={hasRealData ? "var(--color-count)" : "hsl(var(--chart-2) / 0.4)"} 
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>


      {/* Quick Actions */}
      <Card>
        <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
          <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-4 sm:py-5">
            <CardTitle>{t('overview.quickActions.title')}</CardTitle>
            <CardDescription>{t('overview.quickActions.description')}</CardDescription>
          </div>
          <div className="flex">
            <div className="flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left sm:border-l sm:border-t-0 sm:px-8 sm:py-5">
              <span className="text-xs text-muted-foreground">{t('overview.quickActions.quickOperations')}</span>
              <span className="text-lg font-bold leading-none sm:text-2xl">2</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button 
              variant="outline" 
              className="h-20 flex-col"
              asChild
            >
              <a href={`${languagePrefix}/dashboard/memories`}>
                <Database className="h-5 w-5 mb-2 text-blue-600" />
                <span className="text-sm font-medium">{t('overview.quickActions.viewMemory')}</span>
              </a>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col"
              asChild
            >
              <a href={`${languagePrefix}/dashboard/usage`}>
                <BarChart3 className="h-5 w-5 mb-2 text-orange-600" />
                <span className="text-sm font-medium">{t('overview.quickActions.viewStats')}</span>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}


